import type { ComponentType } from 'preact';
import type { LucideProps } from 'lucide-preact';
import { computed, signal } from '@preact/signals';

/** A view inside a section. Pages keep their URLs; the section groups them. */
export interface SectionTab {
  id: string;
  label: string;
  path: string;
  /** Extra URL prefixes that belong to this tab (sub-pages, legacy URLs). */
  matches?: string[];
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: ComponentType<LucideProps>;
  badge?: string;
  openInNewWindow?: boolean;
  /** Section views, shown in the section bar under the top bar. */
  tabs?: SectionTab[];
  /** Extra URL prefixes of pages that belong to the section without being a view. */
  matches?: string[];
}

export interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

import {
  Clapperboard,
  CircleDollarSign,
  Cog,
  MousePointerClick,
  Sparkles,
  Target,
  TrendingUp,
  User as UserIcon,
  Users,
} from 'lucide-preact';

// Estructura 2026-09-17 (docs/00-meta/app-structure.md): seis secciones que siguen el árbol
// de decisión del método y dos de soporte. Una sola fuente para menú, barra de sección,
// breadcrumb y paleta. Las URLs no cambian: una sección agrupa páginas de rutas distintas.
// Retiradas el 2026-09-18 (redirigen a '/'): /reporting (resumen QQ),
// /dapoa-v2, /dashboard-v2 y /resumen-anterior. /reporting/semanal vuelve el 2026-10-14
// como revisión semanal con hitos (ficha 24 de Quique).
export const NAV: NavGroup[] = [
  {
    id: 'principal',
    label: '',
    items: [{ id: 'control', label: 'Control de marketing', path: '/', icon: TrendingUp }],
  },
  {
    id: 'areas',
    label: 'Diagnóstico',
    items: [
      {
        id: 'creativos',
        label: 'Creativos',
        path: '/creativos',
        icon: Clapperboard,
        tabs: [
          { id: 'resumen', label: 'Resumen', path: '/creativos' },
          { id: 'meta-ads', label: 'Meta Ads', path: '/creativos/meta-ads' },
          {
            id: 'creative-intelligence',
            label: 'Creative Intelligence',
            path: '/creativos/creative-intelligence',
          },
          { id: 'laboratorio', label: 'Laboratorio creativo', path: '/creativos/laboratorio' },
          { id: 'core-method', label: 'Núcleo del método', path: '/reporting/core-method' },
          { id: 'funnel', label: 'Rupturas de funnel', path: '/reporting/funnel' },
          { id: 'core-economic', label: 'Economía por creativa', path: '/reporting/core-economic' },
          { id: 'hooks', label: 'Ganchos', path: '/reporting/hooks' },
          {
            id: 'biblioteca',
            label: 'Biblioteca',
            path: '/biblioteca/campanas',
            matches: ['/biblioteca'],
          },
        ],
      },
      {
        id: 'web',
        label: 'Web y conversión',
        path: '/web',
        icon: MousePointerClick,
        tabs: [
          { id: 'resumen', label: 'Resumen', path: '/web' },
          { id: 'funnel', label: 'Landings y funnel', path: '/web/funnel' },
          { id: 'clarity', label: 'Comportamiento', path: '/web/comportamiento' },
          { id: 'products', label: 'Productos y ofertas', path: '/web/productos-ofertas' },
        ],
      },
      {
        id: 'clientes',
        label: 'Clientes y repetición',
        path: '/clientes',
        icon: Users,
        tabs: [
          { id: 'resumen', label: 'Resumen', path: '/clientes' },
          { id: 'ltv', label: 'Valor y cohortes', path: '/clientes/ltv' },
          {
            id: 'adquisicion',
            label: 'Adquisición histórica',
            path: '/reporting/historical-acquisition',
          },
          { id: 'klaviyo', label: 'Email', path: '/clientes/klaviyo' },
          { id: 'leads', label: 'Leads', path: '/clientes/leads' },
          { id: 'lista', label: 'Base de clientes', path: '/clientes/lista' },
        ],
      },
      {
        id: 'ventas',
        label: 'Ventas y rentabilidad',
        path: '/dashboard-v2/ventas',
        icon: CircleDollarSign,
        tabs: [
          { id: 'resumen', label: 'Resumen', path: '/dashboard-v2/ventas' },
          { id: 'pedidos', label: 'Pedidos', path: '/pedidos' },
          { id: 'directors', label: 'P&L de marketing', path: '/reporting/directors' },
          { id: 'viabilidad', label: 'Visión económica', path: '/rentabilidad' },
          { id: 'productos', label: 'Por producto', path: '/rentabilidad/productos' },
          { id: 'costes-marketing', label: 'Costes de marketing', path: '/rentabilidad/marketing' },
        ],
      },
      {
        id: 'estrategia',
        label: 'Estrategia IA',
        path: '/reporting/inteligencia',
        icon: Sparkles,
        tabs: [
          { id: 'informes', label: 'Informes', path: '/reporting/inteligencia' },
          { id: 'semanal', label: 'Revisión semanal', path: '/reporting/semanal' },
          { id: 'recomendaciones', label: 'Recomendaciones', path: '/recomendaciones' },
          {
            id: 'validacion',
            label: 'Validación del asistente',
            path: '/reporting/validacion-asistente',
          },
        ],
      },
    ],
  },
  {
    id: 'sistema',
    label: 'Datos y sistema',
    items: [
      {
        id: 'atribucion',
        label: 'Datos y tracking',
        path: '/atribucion',
        icon: Target,
        tabs: [
          { id: 'overview', label: 'Resumen', path: '/atribucion' },
          { id: 'tracking-health', label: 'Salud del tracking', path: '/atribucion/salud' },
          {
            id: 'trazas',
            label: 'Trazabilidad',
            path: '/atribucion/trazas',
            matches: ['/atribucion/orders', '/atribucion/ads'],
          },
          { id: 'huerfanos', label: 'Huérfanos', path: '/atribucion/huerfanos' },
          { id: 'tracking-review', label: 'Auditoría de tracking', path: '/atribucion/revision' },
          { id: 'url-tags', label: 'Etiquetas de URL', path: '/atribucion/url-tags' },
          { id: 'dedupe-suggestions', label: 'Deduplicación IA', path: '/atribucion/dedupe' },
        ],
      },
      {
        id: 'configuracion',
        label: 'Configuración',
        path: '/configuracion',
        icon: Cog,
        matches: ['/rentabilidad/configuracion', '/rentabilidad/setup'],
      },
      { id: 'cuenta', label: 'Mi cuenta', path: '/cuenta', icon: UserIcon },
    ],
  },
];

const FLAT: NavItem[] = NAV.flatMap((g) => g.items);

function belongsTo(path: string, prefix: string): boolean {
  return prefix === '/' ? path === '/' : path === prefix || path.startsWith(`${prefix}/`);
}

function tabPrefixes(tab: SectionTab): string[] {
  return [tab.path, ...(tab.matches ?? [])];
}

/** Section and view of a path: the longest matching prefix across all sections wins. */
export function resolveSection(path: string): { item: NavItem; tab: SectionTab | null } | null {
  let best: { item: NavItem; tab: SectionTab | null; length: number } | null = null;
  for (const item of FLAT) {
    const candidates: [SectionTab | null, string][] = [
      [null, item.path],
      ...(item.matches ?? []).map((prefix): [null, string] => [null, prefix]),
      ...(item.tabs ?? []).flatMap((tab) =>
        tabPrefixes(tab).map((prefix): [SectionTab, string] => [tab, prefix]),
      ),
    ];
    for (const [tab, prefix] of candidates) {
      if (belongsTo(path, prefix) && (!best || prefix.length > best.length)) {
        best = {
          item,
          tab: tab ?? item.tabs?.find((t) => t.path === item.path) ?? null,
          length: prefix.length,
        };
      }
    }
  }
  return best && { item: best.item, tab: best.tab };
}

export const currentPath = signal('/');

export function setCurrentPath(path: string): void {
  if (currentPath.value !== path) currentPath.value = path;
}

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

// URL slugs van sin tildes (`/configuracion`, `/atribucion`, ...) pero los
// breadcrumbs deben mostrarse en español correcto. Map explícito para
// segmentos conocidos; resto cae al fallback con primera letra mayúscula.
const SEGMENT_LABELS: Record<string, string> = {
  configuracion: 'Configuración',
  atribucion: 'Atribución',
  huerfanos: 'Huérfanos',
  contabilidad: 'Shopify',
  'shopify-ordenes': 'Pedidos',
  'shopify-clientes': 'Clientes',
  transacciones: 'Transacciones',
  meta: 'Meta',
  gasto: 'Gasto Meta',
  rentabilidad: 'Rentabilidad',
  pedidos: 'Pedidos',
  metricas: 'Métricas',
  composicion: 'Composición',
  sincronizaciones: 'Sincronizaciones',
  integraciones: 'Integraciones',
  // El path es `/configuracion/reglas` por razones legacy (era "reglas de
  // nomenclatura"), pero el sidebar + la página se llaman "Nomenclatura".
  // El breadcrumb se alinea al label visible, no al slug crudo.
  reglas: 'Nomenclatura',
  // /economia/profitabilidad: sidebar + título = "Rentabilidad por SKU".
  // Slug es Spanglish por motivos legacy; breadcrumb usa el label visible.
  profitabilidad: 'Rentabilidad por SKU',
  cohortes: 'Cohortes de clientes',
  // /reporting/historical-acquisition + /reporting/directors ya mapeados
  // arriba.  Aquí completamos /economia/* y otros leftover slugs.
  campos: 'Campos',
  empresas: 'Empresas',
  usuarios: 'Usuarios',
  seguridad: 'Seguridad',
  facturacion: 'Facturación',
  resumen: 'Resumen',
  audit: 'Auditoría',
  setup: 'Configuración inicial',
  perfil: 'Perfil',
  escenarios: 'Escenarios',
  viabilidad: 'Viabilidad',
  cac: 'CAC',
  ltgp: 'LTGP',
  marketing: 'Marketing',
  // /rentabilidad/productos: rediseño 2026-06-05 — una entrada "Por
  // producto" con tabs Catálogo y costes / Rentabilidad por SKU.
  productos: 'Por producto',
  runway: 'Autonomía',
  cuenta: 'Mi cuenta',
  // Slugs de Atribución renombrados en Fase 5 (los viejos redirigen).
  salud: 'Tracking health',
  dedupe: 'Deduplicación IA',
  revision: 'Auditoría Tracking',
  'core-method': 'Core method',
  'core-economic': 'Economía',
  funnel: 'Embudo',
  directors: 'Dirección',
  'validacion-asistente': 'Validación del asistente',
  hooks: 'Ganchos',
  'historical-acquisition': 'Adquisición histórica',
  ads: 'Ads',
  adsets: 'Ad sets',
  campaigns: 'Campañas',
  campanas: 'Campañas',
  'ad-sets': 'Ad sets',
  reporting: 'Informes',
  'dashboard-v2': 'Dashboard V2',
  fiabilidad: 'Fiabilidad del dato',
  ejecutivo: 'Resumen ejecutivo',
  ventas: 'Ventas y rentabilidad',
  web: 'Web y conversión',
  comportamiento: 'Comportamiento',
  'meta-ads': 'Meta Ads',
  'creative-intelligence': 'Creative Intelligence',
  creativos: 'Creativos',
  'landings-funnel': 'Landings y funnel',
  clarity: 'Comportamiento (Clarity)',
  'productos-ofertas': 'Productos y ofertas',
  'clientes-ltv': 'Clientes y LTV',
  ltv: 'Clientes y LTV',
  klaviyo: 'Klaviyo',
  'inventario-marketing': 'Inventario × marketing',
  'presupuesto-pacing': 'Presupuesto y pacing',
  'tests-aprendizajes': 'Tests y aprendizajes',
  conclusiones: 'Conclusiones',
  biblioteca: 'Biblioteca',
  leads: 'Leads',
  inicio: 'Inicio',
  ordenes: 'Pedidos',
  clientes: 'Clientes',
  lista: 'Lista',
  'capi-admin': 'CAPI',
  auditoria: 'Auditoría',
  recomendaciones: 'Recomendaciones',
  'url-tags': 'Etiquetas de URL',
  'cupones-internos': 'Cupones internos',
  // Atribución absorbió el antiguo Trazado piloto (Fase 2 rediseño nav).
  trazas: 'Trazabilidad',
  orders: 'Pedido',
};

/** Título de las páginas que una sección incluye por `matches` (el del menú lateral). */
const MATCHED_PAGE_LABELS: Record<string, string> = {
  '/rentabilidad/configuracion': 'Perfil y estrategias',
  '/rentabilidad/setup': 'Configuración inicial',
};

/** Título de páginas cuyo último segmento no basta: debe coincidir con su H1. */
const PATH_LABELS: Record<string, string> = {
  '/configuracion/integraciones/meta': 'Meta Ads',
  '/configuracion/integraciones/analytics': 'Google Analytics 4',
  '/configuracion/integraciones/clarity': 'Microsoft Clarity',
  '/configuracion/integraciones/holded': 'Holded',
};

function labelForSegment(segment: string): string {
  const known = SEGMENT_LABELS[segment];
  if (known) return known;
  const text = segment.replace(/[-_]+/g, ' ');
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export const activeSection = computed(() => resolveSection(currentPath.value));

export const breadcrumb = computed<BreadcrumbItem[]>(() => {
  // Solo destinos navegables: "Sección › Vista" cuando la ruta pertenece a una sección
  // (las vistas pueden vivir en URLs de otros dominios, p. ej. /reporting/hooks en
  // Creatividades); si no, los segmentos reales del path.
  const path = currentPath.value;
  if (path === '/' || path === '') return [{ label: 'Control de marketing' }];
  const section = activeSection.value;
  if (section && section.item.path !== '/') {
    const { item, tab } = section;
    const crumbs: BreadcrumbItem[] = [{ label: item.label, href: item.path }];
    if (tab && tab.path !== item.path) crumbs.push({ label: tab.label, href: tab.path });
    const base = tab?.path ?? item.path;
    if (path !== base && path.startsWith(`${base}/`)) {
      const rest = path
        .slice(base.length + 1)
        .split('/')
        .filter(Boolean);
      const last = rest[rest.length - 1];
      if (last) crumbs.push({ label: PATH_LABELS[path] ?? labelForSegment(last) });
    } else if (path !== base) {
      // Páginas que pertenecen a la sección por `matches` pero viven en otra URL
      // (p. ej. /rentabilidad/setup dentro de Configuración): sin esto el breadcrumb
      // se quedaba en "Configuración" y no decía en qué página estás.
      const last = path.split('/').filter(Boolean).pop();
      const label = MATCHED_PAGE_LABELS[path] ?? (last ? labelForSegment(last) : null);
      if (label) crumbs.push({ label });
    }
    const final = crumbs[crumbs.length - 1];
    if (final) final.href = undefined;
    return crumbs;
  }
  const parts = path.split('/').filter(Boolean);
  return parts.map((segment, i) => {
    const isLast = i === parts.length - 1;
    const href = '/' + parts.slice(0, i + 1).join('/');
    return {
      label: (isLast && PATH_LABELS[path]) || labelForSegment(segment),
      href: isLast ? undefined : href,
    };
  });
});

export const activeNavId = computed(() => activeSection.value?.item.id ?? null);

export const pageTitle = computed(() => activeSection.value?.item.label ?? 'Onmi');
